import { useCallback, useState } from 'react';
import BackendService from '../../../services/admin/BackendService';
import { InvestorStatementItem } from '../../../models/admin/InvestorStatement';

type ToastFn = (message: string, type: 'error' | 'warning' | 'info' | 'success') => void;
type UpdateItemFn = (externalId: string, patch: Partial<InvestorStatementItem>) => void;

/**
 * Confirm (with replace-warning) -> send-prompt -> compose sequencing for
 * the statement review list. Kept together because confirming successfully
 * is what triggers the send prompt — splitting confirm and send into
 * unrelated hooks would just mean one calling back into the other's setter.
 */
export function useStatementConfirmAndSendActions(
  items: InvestorStatementItem[],
  backendRef: React.MutableRefObject<BackendService>,
  updateItem: UpdateItemFn,
  setBusyItemId: (id: string | null) => void,
  onShowToast: ToastFn,
) {
  const [sendPromptItemId, setSendPromptItemId] = useState<string | null>(null);
  const [composeItemId, setComposeItemId] = useState<string | null>(null);
  const [replaceWarningItemId, setReplaceWarningItemId] = useState<string | null>(null);

  const doConfirm = useCallback(
    async (item: InvestorStatementItem) => {
      setBusyItemId(item.externalId);
      try {
        const confirmed = await backendRef.current.confirmInvestorStatement(item.externalId);
        updateItem(item.externalId, confirmed);
        setSendPromptItemId(item.externalId);
      } catch (err: unknown) {
        onShowToast(err instanceof Error ? err.message : 'Failed to confirm statement', 'error');
      } finally {
        setBusyItemId(null);
      }
    },
    [backendRef, onShowToast, setBusyItemId, updateItem],
  );

  // Confirming replaces any existing confirmed statement for this investor+entity
  // (see InvestorStatementGenerationService.confirm) — warn before it happens
  // rather than silently overwriting.
  const handleConfirm = useCallback(
    (item: InvestorStatementItem) => {
      if (item.willReplaceExistingConfirmedStatement) {
        setReplaceWarningItemId(item.externalId);
        return Promise.resolve();
      }
      return doConfirm(item);
    },
    [doConfirm],
  );

  const cancelReplace = useCallback(() => setReplaceWarningItemId(null), []);
  const confirmReplace = useCallback(() => {
    const item = items.find((i) => i.externalId === replaceWarningItemId);
    setReplaceWarningItemId(null);
    if (item) void doConfirm(item);
  }, [doConfirm, items, replaceWarningItemId]);

  const declineSend = useCallback(() => setSendPromptItemId(null), []);
  const acceptSend = useCallback(() => {
    setComposeItemId(sendPromptItemId);
    setSendPromptItemId(null);
  }, [sendPromptItemId]);
  const closeCompose = useCallback(() => setComposeItemId(null), []);

  const markSent = useCallback(
    (externalId: string) => {
      updateItem(externalId, { emailSentAt: new Date().toISOString() });
      setComposeItemId(null);
    },
    [updateItem],
  );
  return {
    sendPromptItemId,
    composeItemId,
    replaceWarningItemId,
    handleConfirm,
    cancelReplace,
    confirmReplace,
    declineSend,
    acceptSend,
    closeCompose,
    markSent,
  };
}
